import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardHeader, ContentLoader, Input } from '@/components/common';

function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
      <p>We could not load this. Check your connection and try again.</p>
      <Button className="mt-3" size="sm" variant="outline" onClick={onRetry}>Retry</Button>
    </div>
  );
}
import { partnerPortalApi } from '@/services/partnerPortal';
import { useAuthStore } from '@/store/auth';

type Tab = 'agents' | 'production' | 'targets' | 'invites';

const TABS: { id: Tab; label: string }[] = [
  { id: 'agents', label: 'My agents' },
  { id: 'production', label: 'Production' },
  { id: 'targets', label: 'Targets' },
  { id: 'invites', label: 'Invite an agent' },
];

const currentPeriod = () => new Date().toISOString().slice(0, 7);

/**
 * Channel partner portal, phase 1. A partner sees their OWN agents and those agents' business as
 * totals. It never shows a customer, a policy number or an agent's private details, and there is
 * nothing here that pays or calculates any share for the partner.
 */
export function PartnerPortalPage() {
  const [tab, setTab] = useState<Tab>('agents');
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const me = useQuery({ queryKey: ['partner', 'me'], queryFn: partnerPortalApi.me, retry: false });

  if (me.isLoading) return <ContentLoader />;
  if (me.isError || !me.data) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Partner access unavailable</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This login is not active. Please contact your broker.
        </p>
      </div>
    );
  }
  const profile = me.data;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{profile.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {profile.partner_type} · {profile.code} · {profile.broker_name}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            logout();
            navigate('/login/broker');
          }}
        >
          Sign out
        </Button>
      </header>

      <nav className="mb-6 flex gap-2 overflow-x-auto border-b border-gray-200 dark:border-gray-700" aria-label="Partner sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-current={tab === t.id ? 'page' : undefined}
            className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'agents' && <AgentsTab />}
      {tab === 'production' && <ProductionTab />}
      {tab === 'targets' && <TargetsTab />}
      {tab === 'invites' && <InvitesTab />}
    </div>
  );
}

function AgentsTab() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['partner', 'posps'], queryFn: partnerPortalApi.posps });
  if (isLoading) return <ContentLoader />;
  if (isError) return <LoadError onRetry={() => refetch()} />;
  return (
    <Card>
      <CardHeader
        title="My agents"
        subtitle="Agents who joined through your invite. The commission shown is what each agent earned on their own business."
      />
      {!data?.length ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No agents yet. Invite one from the next tab.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 text-right">Commission earned by the agent</th>
              </tr>
            </thead>
            <tbody className="text-gray-900 dark:text-gray-100">
              {data.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-4">{p.posp_code}</td>
                  <td className="py-2 pr-4">{p.name}</td>
                  <td className="py-2 pr-4">{p.status_display}</td>
                  <td className="py-2 text-right tabular-nums">{p.posp_commission_earned}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ProductionTab() {
  const [period, setPeriod] = useState(currentPeriod());
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['partner', 'production', period],
    queryFn: () => partnerPortalApi.production(period),
  });
  return (
    <Card>
      <CardHeader
        title="Production"
        subtitle="Issued and active policies only. Cancelled, lapsed and expired policies are not counted."
      />
      <div className="mb-4 max-w-xs">
        <Input label="Month" type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
      </div>
      {isLoading ? (
        <ContentLoader />
      ) : isError ? (
        <LoadError onRetry={() => refetch()} />
      ) : !data?.results.length ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No agents yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="py-2 pr-4">Agent</th>
                <th className="py-2 pr-4 text-right">Policies</th>
                <th className="py-2 pr-4 text-right">Premium</th>
                <th className="py-2 text-right">Commission earned by the agent</th>
              </tr>
            </thead>
            <tbody className="text-gray-900 dark:text-gray-100">
              {data.results.map((r) => (
                <tr key={r.posp_id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-4">
                    {r.name} <span className="text-gray-500">({r.posp_code})</span>
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.policy_count}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.premium}</td>
                  <td className="py-2 text-right tabular-nums">{r.posp_commission_earned}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function TargetsTab() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['partner', 'targets'], queryFn: partnerPortalApi.targets });
  if (isLoading) return <ContentLoader />;
  if (isError) return <LoadError onRetry={() => refetch()} />;
  return (
    <Card>
      <CardHeader title="Targets" subtitle="Targets set by the broker for your agents, with progress." />
      {!data?.length ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No targets set for your agents.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {data.map((t) => (
            <li key={t.id} className="py-3 text-sm text-gray-900 dark:text-gray-100">
              <div className="flex justify-between gap-4">
                <span>
                  {t.name} <span className="text-gray-500">({t.posp_code})</span>
                </span>
                <span className="tabular-nums">
                  {t.achieved_value} / {t.target_value} ({t.achievement_percentage}%)
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {t.start_date} to {t.end_date}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function InvitesTab() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['partner', 'invites'], queryFn: partnerPortalApi.invites });
  const [form, setForm] = useState({ name: '', mobile: '', email: '' });
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: partnerPortalApi.createInvite,
    onSuccess: (invite) => {
      setLink(invite.invite_url ?? null);
      setError(null);
      setForm({ name: '', mobile: '', email: '' });
      qc.invalidateQueries({ queryKey: ['partner', 'invites'] });
    },
    onError: () => setError('Could not create the invite. Check the details and try again.'),
  });
  const revoke = useMutation({
    mutationFn: partnerPortalApi.revokeInvite,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner', 'invites'] }),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Invite an agent"
          subtitle="They receive a one-time link, valid for 7 days, to complete their application. The broker reviews and approves every application."
        />
        <form
          className="grid gap-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(form);
          }}
        >
          <Input label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Mobile" required inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
          <Input label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <div className="sm:col-span-3">
            <Button type="submit" isLoading={create.isPending}>
              Send invite
            </Button>
          </div>
        </form>
        {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {link && (
          <p role="status" className="mt-3 break-all text-sm text-gray-700 dark:text-gray-300">
            Invite created. This link is shown once: <span className="font-mono">{link}</span>
          </p>
        )}
      </Card>

      <Card>
        <CardHeader title="Your invites" />
        {isLoading ? (
          <ContentLoader />
        ) : isError ? (
          <LoadError onRetry={() => refetch()} />
        ) : !data?.length ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">You have not invited anyone yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {data.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                <div>
                  <div>{i.name}</div>
                  <div className="text-xs text-gray-500">{i.stage_display}</div>
                </div>
                {i.stage === 'pending' && (
                  <Button size="sm" variant="outline" onClick={() => revoke.mutate(i.id)} isLoading={revoke.isPending}>
                    Withdraw
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
